/**
 * 데이터 신선도(staleness) 판정 — 순수 함수만. (TRAD-2149)
 *
 * 장애 대응의 세 번째 상태다. 오늘까지 우리가 갈라낸 건 **실패 / 0건 / 정상** 세 가지인데,
 * 실제로는 네 번째가 있다: **"응답은 성공했지만 데이터가 오래된 것"**.
 * 수집기가 멈춰도 서버는 200 + (낡은) 데이터를 주므로, 화면은 아무 이상 없어 보인다.
 * 그래서 "언제 기준 데이터인지"를 사용자에게 말해줘야 한다.
 *
 * 표시(React)와 판정(여기)을 분리해 둔다 — 판정은 mock 없이 단위 테스트된다.
 */

/** 1분 = 60,000ms */
const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 60 * 24;

/**
 * 🔴 stale 배지를 **붙여도 되는** 데이터 종류.
 *
 * 시세(price/ticker/orderbook)가 **의도적으로 빠져 있다.** 타입으로 제외했으므로
 * `dataKind="price"` 같은 호출은 **컴파일이 실패한다**(주석 규칙이 아니라 컴파일 규칙).
 *
 * 왜 시세는 제외인가 — TRAD-2149 본문:
 * **오래된 시세를 배지 달고 보여주는 것이 아예 안 보여주는 것보다 나쁘다.**
 * 사용자는 숫자를 먼저 읽고 배지는 나중에 읽는다(혹은 안 읽는다). 낡은 가격으로 판단하면
 * 금전적 손해가 나므로, 시세는 stale 이면 **표시를 멈추고 실패로 다루는 것**이 맞다.
 * 반면 파생 지표·김프·부동산·경제지표는 몇 분 늦어도 판단을 뒤집지 않으므로 배지가 낫다.
 */
export type StaleableDataKind =
  | 'derivatives'
  | 'kimchi-premium'
  | 'real-estate'
  | 'economic-indicator'
  | 'ranking';

/**
 * 시세류 — **절대 stale 배지를 붙이지 않는다.** 런타임 방어용 목록(타입 우회 대비).
 * 타입만으로도 막히지만, JS 호출부(테스트·동적 값)에서 새어 들어오는 경우를 한 번 더 막는다.
 */
export const STALE_FORBIDDEN_KINDS = ['price', 'ticker', 'orderbook'] as const;
export type StaleForbiddenKind = (typeof STALE_FORBIDDEN_KINDS)[number];

/** 기본 임계값: 이 분 수 미만이면 "신선"으로 보고 아무것도 표시하지 않는다. */
export const DEFAULT_STALE_THRESHOLD_MINUTES = 10;

/** 경과 분. 미래 시각(시계 오차)은 0으로 클램프한다 — "-3분 전"은 의미가 없다. */
export const minutesSince = (lastUpdatedMs: number, nowMs: number): number =>
  Math.max(0, Math.floor((nowMs - lastUpdatedMs) / MS_PER_MINUTE));

/**
 * 서버 타임존 오프셋. backend 가 오프셋 없는 문자열을 보낼 때 이 값으로 해석한다.
 * (운영·dev 서버 모두 KST 로 동작하고, `LocalDateTime` 을 그대로 직렬화한다.)
 */
const SERVER_UTC_OFFSET = '+09:00';

/** 이미 타임존이 붙어 있는가 — `Z` 또는 `±HH:MM` / `±HHMM`. */
const HAS_TIMEZONE = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * 🔴 TRAD-2444 — 날짜-시각(`T` 포함) 성분이 있는가. **날짜만 있는 문자열("2026-05-21")에는
 * 오프셋을 붙이면 안 된다** — `new Date("2026-05-21+09:00")`는 `Invalid Date`다(실측 확인).
 * ES 규격상 날짜만 있는 형식은 애초에 **UTC 자정**으로 정의돼 모호함이 없다 — 오프셋
 * 없음=로컬 타임존인 것은 오직 "날짜-시각" 형식에서만 참이다. `withServerOffset`을
 * `parseServerDate`(dateDisplay.tsx)가 재사용하면서 날짜만 있는 필드(`VoteHistoryItem.voteDate`
 * 등)가 걸릴 수 있어 추가한 가드다. `collectedAt`처럼 항상 날짜-시각인 소비처는 이 가드로
 * 동작이 바뀌지 않는다(모든 값이 `T`를 포함).
 */
const HAS_TIME_COMPONENT = /T\d{2}:\d{2}/;

/**
 * 🔴 `dataMeta.collectedAt` 파서 — **글로벌 서비스에서 반드시 필요한 보정** (TRAD-2149).
 *
 * ## 무엇이 문제인가 (2026-08-04 dev 실측)
 * 규약 문서는 `"2026-08-04T09:10:00+09:00"`(오프셋 포함)을 약속했지만, 실제 응답은
 * **`"2026-08-04T13:10:26"` — 오프셋이 없다** (backend `DataMeta` 가 `LocalDateTime` 에
 * `@JsonFormat(pattern="yyyy-MM-dd'T'HH:mm:ss")` 만 붙여 둔 탓).
 *
 * ES 규격상 **날짜-시각 형식에 오프셋이 없으면 브라우저 로컬 타임존**으로 파싱된다
 * (날짜만 있는 형식은 UTC — 규격이 이 둘을 다르게 정의한다). 그래서 그냥 `new Date(s)` 하면
 * **KST 사용자만 맞고, UTC 사용자는 같은 데이터를 9시간 미래로 읽는다.** 미래 시각은
 * `minutesSince` 가 0 으로 클램프하므로 **"N분 전" 문구가 영영 뜨지 않는다** — 배지가 조용히
 * 죽는 것이고, 화면은 낡은 데이터를 최신인 양 보여준다. 정확히 이 배지가 막으려던 사고다.
 *
 * ## 어떻게 고치나 — 양쪽 세계에서 모두 옳게
 * - 오프셋이 **있으면** 그대로 믿는다 (backend 가 나중에 오프셋을 붙여도 **이 코드는 그대로 옳다**).
 * - 오프셋이 **없으면** 서버 타임존(KST)으로 해석한다 (지금의 실제 응답).
 *
 * 그래서 backend 수정을 기다리지 않아도 되고, backend 가 고쳐도 **이중 보정으로 깨지지 않는다.**
 * 임시 하드코딩이 아니라 "오프셋 없는 값은 서버 로컬"이라는 항구적으로 참인 규칙이다.
 *
 * @returns epoch ms. 파싱 불가·빈 값이면 `null` (그때는 아무것도 표시하지 않는다).
 */
/**
 * 오프셋이 없으면 서버 타임존을 붙인다. **문자열 → 문자열 순수 변환.**
 *
 * 🔴 이 단계를 따로 export 하는 이유는 **테스트가 실행 타임존에 좌우되지 않게** 하기 위함이다.
 * `parseCollectedAt` 의 결과(epoch ms)로만 검증하면, **KST 머신에서는 보정을 지워도 테스트가
 * 그대로 통과한다** — 그 환경에선 순진한 파싱이 우연히 정답이기 때문이다(2026-08-04 실측:
 * 보정 제거 사보타주가 로컬 KST 에서 22/22 통과, `TZ=UTC` 에서만 2건 실패).
 * 즉 결과 기반 단언만 두면 **KST 에서 도는 CI 는 이 회귀를 영원히 못 잡는다.**
 * 문자열 변환은 타임존과 무관하므로 어디서 돌려도 회귀를 잡는다.
 */
export const withServerOffset = (collectedAt: string): string =>
  HAS_TIMEZONE.test(collectedAt) || !HAS_TIME_COMPONENT.test(collectedAt)
    ? collectedAt
    : `${collectedAt}${SERVER_UTC_OFFSET}`;

export const parseCollectedAt = (collectedAt: string | null | undefined): number | null => {
  if (!collectedAt) return null;
  const ms = new Date(withServerOffset(collectedAt)).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/**
 * stale 여부. 🔴 **경계값은 "임계값 이상"이 stale** 이다(threshold=10 이면 10분도 stale).
 * 임계값 미만은 신선이므로 **아무 표시도 하지 않는다** — 정상을 이상처럼 보이게 하면
 * 그것도 화면이 거짓말하는 것이다(0건을 에러로 바꾸는 것과 같은 잘못).
 */
export const isStale = (
  minutes: number,
  thresholdMinutes: number = DEFAULT_STALE_THRESHOLD_MINUTES,
): boolean => minutes >= thresholdMinutes;

/** 배지 문구를 고르기 위한 단위 분해. 1440분을 "1440분 전"이라 쓰지 않기 위함. */
export type StaleAmount =
  | { unit: 'minutes'; value: number }
  | { unit: 'hours'; value: number }
  | { unit: 'days'; value: number };

export const toStaleAmount = (minutes: number): StaleAmount => {
  if (minutes < MINUTES_PER_HOUR) return { unit: 'minutes', value: minutes };
  if (minutes < MINUTES_PER_DAY) return { unit: 'hours', value: Math.floor(minutes / MINUTES_PER_HOUR) };
  return { unit: 'days', value: Math.floor(minutes / MINUTES_PER_DAY) };
};
